import React from "react";
import { ChevronDown } from "lucide-react";
import { restaurantInfo } from "../data/mock";

/**
 * Hero — editorial full-bleed video with a giant serif headline.
 * Muted + looped + autoplay attributes required for iOS Safari autoplay.
 * The bottom gradient melts the media into the sand-coloured page.
 */
export default function Hero() {
  return (
    <section id="top" className="relative min-h-[100vh] w-full overflow-hidden">
      {/* Background media */}
      <div className="absolute inset-0">
        <video
          className="w-full h-full object-cover"
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          poster="/media/community-1-poster.jpg"
          aria-hidden="true"
          data-testid="hero-video"
        >
          <source src="/media/community-1.mp4" type="video/mp4" />
          <source src="/media/community-2.mp4" type="video/mp4" />
        </video>
        {/* Even scrim for legibility, then a fade into the sand page below. */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/45 to-black/50" />
        <div className="absolute bottom-0 inset-x-0 h-80 bg-gradient-to-b from-transparent via-[#F6F5F2]/60 to-[#F6F5F2]" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 pt-36 md:pt-44 pb-24 min-h-[100vh] flex flex-col justify-between">
        <div className="flex flex-col items-center text-center">
          <div className="flex items-center gap-4 fade-in visible">
            <span className="h-px w-10 bg-white/50 hidden md:block" />
            <span className="text-[0.68rem] tracking-[0.34em] uppercase text-white/85 font-medium">
              Acacia Ridge · Brisbane · 100% Halal
            </span>
            <span className="h-px w-10 bg-white/50 hidden md:block" />
          </div>

          <h1 className="hero-title mt-10 text-[11vw] md:text-[7.5vw] text-white">
            Cooked with fire.
            <br />
            Served with soul.
          </h1>

          <p className="mt-8 max-w-xl text-white/80 text-base md:text-lg leading-relaxed font-light">
            Brisbane&apos;s taste of Liberia. An open kitchen rooted in West Africa,
            led by warmth, and made to be shared.
          </p>

          <div className="mt-12 flex flex-wrap items-center justify-center gap-10">
            <a
              href="#reservation"
              data-testid="hero-cta-reserve"
              className="text-white text-sm tracking-[0.14em] lowercase border-b border-white/50 pb-1 hover:border-white transition-colors duration-300"
            >
              reserve a table
            </a>
            <a
              href="#order"
              data-testid="hero-cta-order"
              className="text-white text-sm tracking-[0.14em] lowercase border-b border-white/50 pb-1 hover:border-white transition-colors duration-300"
            >
              order pickup
            </a>
          </div><div className="mt-6 flex flex-wrap items-center justify-center gap-4" aria-label="Delivery ordering">{restaurantInfo.delivery.filter((s) => ["Uber Eats", "DoorDash"].includes(s.name)).map((s) => (<a key={s.name} href={s.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center rounded border border-white/70 px-5 py-3 text-sm font-medium text-white hover:bg-white/15">Order on {s.name}</a>))}</div>
        </div>

        <div className="hidden md:flex items-end justify-between mt-16">
          <div className="flex items-center gap-3 text-[#3d382f]">
            <ChevronDown size={18} className="animate-bounce" />
            <span className="text-xs tracking-[0.3em] uppercase">Scroll</span>
          </div>
          <div className="text-right">
            <div className="text-[0.68rem] tracking-[0.3em] uppercase text-[#5B564E] mb-1">Rated on Google</div>
            <div className="font-display text-2xl text-[#1A1A1A]">4.9 ★ · 38 Reviews</div>
          </div>
        </div>
      </div>
    </section>
  );
}
